import { FC, useCallback, useEffect, useState } from 'react';

import { FileDTO } from '@/types/api-types';

import IconClose from '@/assets/svg/icon-close-out.svg?react';
import IconMaximize from '@/assets/svg/icon-expand.svg?react';

import { cn } from '@/lib/utils';

import { useIsMobile } from '@/hooks/use-mobile';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog';

import { useApiManagerStore } from '@/stores/apiManagerStore';
import { useFileExplorerStore } from '@/stores/fileExplorerStore';

import { ExplorerItemType } from '@/constants/table-columns-config';

import ComparisonView from './ComparisonView';
import ExplorerView from './ExplorerView';
import FileSelectionHeader from './FileSelectionHeader';

export interface ImageState extends FileDTO {
  transform: { scale: number; rotation: number };
  onTransform: (transform: { scale: number; rotation: number }) => void;
}

interface ImageCompareDialogProps {
  isOpen: boolean;
  onClose: () => void;
  initialLeftImageUUID?: string;
  currentUUID: string;
}

export const ImageCompareDialog: FC<ImageCompareDialogProps> = ({
  isOpen,
  onClose,
  initialLeftImageUUID,
  currentUUID,
}) => {
  const { filesState } = useFileExplorerStore();
  const { files } = useApiManagerStore();
  const isMobile = useIsMobile();

  const [isMaximized, setIsMaximized] = useState(isMobile);

  const [leftImage, setLeftImage] = useState<ImageState | null>(null);
  const [rightImage, setRightImage] = useState<ImageState | null>(null);

  const [leftExplorer, setLeftExplorer] = useState({
    isEnabled: true,
    currentUUID: currentUUID,
    entityType: ExplorerItemType.FILE,
  });
  const [rightExplorer, setRightExplorer] = useState({
    isEnabled: false,
    currentUUID: currentUUID,
    entityType: ExplorerItemType.FILE,
  });

  const handleMaximize = () => setIsMaximized((prev) => !prev);

  const handleSwapImages = () => {
    if (leftImage && rightImage) {
      setLeftImage(rightImage);
      setRightImage(leftImage);
    }
  };

  const handleFileSelect = (side: 'left' | 'right') => () => {
    if (side === 'left') {
      setLeftImage(null);
      setLeftExplorer({
        isEnabled: true,
        currentUUID: currentUUID,
        entityType: ExplorerItemType.FILE,
      });
    } else {
      setRightImage(null);
      setRightExplorer({
        isEnabled: true,
        currentUUID: currentUUID,
        entityType: ExplorerItemType.FILE,
      });
    }
  };

  const handleImageClick = (side: 'left' | 'right') => (uuid: string) => {
    const file = files.find((file) => file.uuid === uuid);
    if (!file) return;

    const newImage: ImageState = {
      ...file,
      transform: { scale: 1, rotation: 0 },
      onTransform: (transform) => {
        if (side === 'left') {
          setLeftImage((prev) =>
            prev ? ({ ...prev!, transform } as ImageState) : null,
          );
        } else {
          setRightImage((prev) =>
            prev ? ({ ...prev!, transform } as ImageState) : null,
          );
        }
      },
    };

    if (side === 'left') {
      setLeftImage(newImage);
    } else {
      setRightImage(newImage);
    }
  };

  const setInitialLeftImageState = useCallback(() => {
    if (initialLeftImageUUID) {
      const file = filesState.entities[initialLeftImageUUID];
      if (file) {
        setLeftImage({
          ...file,
          transform: { scale: 1, rotation: 0 },
          onTransform: () => {},
        });
      }
    }
  }, [initialLeftImageUUID, filesState.entities]);

  // Reset all state when dialog closes
  useEffect(() => {
    // Initialize state when dialog opens
    if (isOpen) {
      setIsMaximized(isMobile);
      // Only set initial left image if it's provided and we don't already have one
      if (initialLeftImageUUID) {
        setInitialLeftImageState();
      }
    } else {
      // Clear state when dialog closes with a small delay to ensure proper cleanup
      const timer = setTimeout(() => {
        setIsMaximized(false);
        setLeftImage(null);
        setRightImage(null);
        setLeftExplorer({
          isEnabled: true,
          currentUUID: currentUUID,
          entityType: ExplorerItemType.FILE,
        });
        setRightExplorer({
          isEnabled: false,
          currentUUID: currentUUID,
          entityType: ExplorerItemType.FILE,
        });
      }, 50);

      return () => clearTimeout(timer);
    }
  }, [
    isOpen,
    currentUUID,
    initialLeftImageUUID,
    isMobile,
    setInitialLeftImageState,
  ]);

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent
        className={cn(
          'flex flex-col gap-0 border-none bg-white p-0 [&_#dialog-close]:hidden',
          isMaximized
            ? 'h-screen w-screen max-w-none'
            : 'h-[100vh] max-h-[100vh] w-[1500px] max-w-[100vw] sm:h-auto sm:max-h-[90vh] sm:max-w-[95vw]',
        )}
      >
        <DialogTitle className="mb-3 text-2xl font-semibold" hidden>
          Порівняння зображень
        </DialogTitle>
        <div className="flex items-center justify-between p-5">
          <h2 className="text-2xl font-semibold text-primary">
            Порівняння зображень
          </h2>
          <div className="flex items-center gap-4">
            <button
              className="transition-colors hover:[&_svg]:fill-primary"
              onClick={handleMaximize}
              hidden={isMobile}
              aria-label="Toggle maximize"
            >
              <IconMaximize className="h-[20px] w-[20px] fill-white transition-colors" />
            </button>
            <button
              className="transition-colors hover:[&_svg]:fill-primary"
              onClick={onClose}
              aria-label="Close dialog"
            >
              <IconClose className="h-[20px] w-[20px] fill-white transition-colors" />
            </button>
          </div>
        </div>

        <div className="flex flex-grow flex-col overflow-y-auto p-5 pt-0 sm:overflow-y-hidden">
          {!isMobile && (
            <FileSelectionHeader
              onLeftSelect={handleFileSelect('left')}
              onRightSelect={handleFileSelect('right')}
              onSwap={handleSwapImages}
              leftFilename={leftImage?.name}
              rightFilename={rightImage?.name}
            />
          )}

          {leftImage !== null && rightImage !== null ? (
            <ComparisonView
              leftImage={leftImage}
              rightImage={rightImage}
              onLeftTransform={(transform) =>
                setLeftImage((prev) => (prev ? { ...prev, transform } : null))
              }
              onRightTransform={(transform) =>
                setRightImage((prev) => (prev ? { ...prev, transform } : null))
              }
              onLeftSelect={handleFileSelect('left')}
              onRightSelect={handleFileSelect('right')}
            />
          ) : (
            <ExplorerView
              leftExplorer={leftExplorer}
              rightExplorer={rightExplorer}
              onLeftUpdate={setLeftExplorer}
              onRightUpdate={setRightExplorer}
              onImageSelect={handleImageClick}
              handleFileSelect={handleFileSelect}
              leftImage={leftImage}
              rightImage={rightImage}
            />
          )}
        </div>
      </DialogContent>
      <DialogDescription hidden>Порівняння зображень</DialogDescription>
    </Dialog>
  );
};
